import { bootstrapApplication } from '@angular/platform-browser';
import { appConfig } from './app/app.config';
import { AppComponent } from './app/app.component';
import { configureSecureMarked } from './app/shared/markdown/secure-marked-options';
import DOMPurify from 'dompurify';

// Trusted Types: ngx-markdown assigns a plain string to innerHTML (no TT support).
// The `default` policy is the browser's catch-all — it intercepts every bare-string
// sink assignment that is not already a TrustedHTML object and sanitizes it through
// DOMPurify before the write is allowed.
if ((window as any).trustedTypes?.createPolicy) {
  (window as any).trustedTypes.createPolicy('default', {
    createHTML: (input: string) =>
      DOMPurify.sanitize(input, {
        USE_PROFILES: { html: true, svg: true, svgFilters: true },
        // Mermaid 10 renders flowchart labels inside <foreignObject> (HTML-in-SVG).
        // DOMPurify's SVG profile excludes it by default; adding it here keeps labels
        // visible while DOMPurify still sanitizes the HTML content inside.
        ADD_TAGS: ['foreignObject'],
        ADD_ATTR: ['target'],
      }) as string,
    createScriptURL: (input: string) => input,
    createScript: (input: string) => input,
  });
}

// MSAL silent-iframe guard: when acquireTokenSilent falls back to a hidden
// iframe it navigates to our redirectUri (the app root), which would normally
// re-bootstrap Angular. In @azure/msal-browser v5 the parent frame waits for
// the iframe to broadcast the auth response over a BroadcastChannel (there is
// no hash-polling fallback), so the iframe MUST run the redirect bridge instead
// of the full app — otherwise the parent times out and every request goes out
// unauthenticated.
//
// Detection: we are framed or in a popup AND the URL carries an OAuth response
// (`code`/`error` + `state`) — the exact signal `broadcastResponseToMainFrame`
// parses. NOTE: we deliberately do NOT key off MSAL's `*.interaction.status`
// sessionStorage entry: that key is written only by the interactive redirect
// client (login / logout), never by the silent-iframe client, so it is absent
// during silent renewal. Both conditions are required so that legitimate iframe
// embedding without an auth response still boots the app, and the top-frame
// interactive-login redirect (has a response but is not framed) is unaffected.
const authResponse = `${window.location.hash}&${window.location.search}`;
const hasMsalAuthResponse =
  /[#?&](code|error)=/.test(authResponse) && /[#?&]state=/.test(authResponse);
const isMsalSilentIframe =
  (window.parent !== window || Boolean(window.opener)) && hasMsalAuthResponse;

if (isMsalSilentIframe) {
  // Run the MSAL redirect bridge and stop. The bridge broadcasts the auth
  // response back to the main frame via BroadcastChannel so the original
  // acquireTokenSilent call can resolve.
  import('@azure/msal-browser/redirect-bridge')
    .then(({ broadcastResponseToMainFrame }) => broadcastResponseToMainFrame())
    .catch(console.error);
} else {
  // Security: enforce the markdown-only / raw-HTML-escaping policy on the global
  // `marked` singleton before anything renders (pentest Finding 01, CWE-74).
  configureSecureMarked();

  bootstrapApplication(AppComponent, appConfig).catch((err) => {
    console.error('Bootstrap failed:', err);
    const el = document.createElement('div');
    el.style.cssText = 'padding:20px;font-family:sans-serif';
    const h = document.createElement('h1');
    h.style.color = '#D32F2F';
    h.textContent = 'Application Failed to Start';
    const p = document.createElement('p');
    p.textContent = 'Please check the console for details.';
    el.append(h, p);
    document.body.replaceChildren(el);
  });
}
